import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  CheckboxCircleArrowIcon,
  ConfirmDialog,
  DescriptionIcon,
  EditIcon,
  IconButton,
  List,
  ListItem,
  ListSeparator,
  Menu,
  TrashIcon,
  TreeDotsIcon,
} from "@activecollab/components";
import classnames from "classnames";
import PropTypes from "prop-types";
import { CREATE_TASK_FROM_COMMENT_ERROR, CREATE_TASK_FROM_COMMENT_SUCCESS } from "~/store/action_types";
import { createTaskFromComment } from "~/store/actions/actions.comments";
import { getParentOfComment } from "~/store/selectors/selector.comments";
import { AppendDialog } from "./AppendDialog";
import { CreateTaskDialog } from "./CreateTaskDialog";
import { useSecurity } from "../Hooks";
import { showErrorToast, showSuccessToast } from "../Toast";

export const CommentOptions = ({ comment, onEdit, onDelete }) => {
  const { t } = useTranslation();
  const { id } = comment;
  const dispatch = useDispatch();
  // menu state
  const [openMenu, setOpenMenu] = useState(false);
  const handleOpen = useCallback(() => setOpenMenu(true), []);
  const handleClose = useCallback(() => setOpenMenu(false), []);

  // dialog
  const [dialogState, setDialogState] = useState(undefined);
  const closeDialog = useCallback(() => {
    setDialogState(undefined);
  }, []);
  const closeDialogAndMenu = useCallback(() => {
    setOpenMenu(false);
    setDialogState(undefined);
  }, []);

  // voters
  const can = useSecurity();
  const canEdit = can(["edit"], comment);
  const canDelete = can(["delete"], comment);
  const canCreateTask = can(["create-task"], comment);

  const parent = useSelector((state) => getParentOfComment(state, comment.parent_type, comment.parent_id));

  const canAppend = can(["edit"], parent);
  const showOption = canEdit || canDelete || canCreateTask || canAppend;

  const triggerEditComment = useCallback(() => {
    if (typeof onEdit === `function`) {
      onEdit(id);
    }
  }, [id, onEdit]);

  const triggerDeleteComment = useCallback(() => {
    if (typeof onDelete === `function`) {
      onDelete(id);
      setOpenMenu(false);
    }
  }, [id, onDelete]);

  const onCreateTask = useCallback(
    ({ name, project_id }) => {
      dispatch(createTaskFromComment(id, { name, project_id })).then((action) => {
        if (action.type === CREATE_TASK_FROM_COMMENT_SUCCESS) {
          let message = "Task created";

          showSuccessToast(message);
        } else if (action.type === CREATE_TASK_FROM_COMMENT_ERROR) {
          showErrorToast(t("Failed to create task"));
        }
        closeDialogAndMenu();
      });
    },
    [dispatch, id, closeDialogAndMenu, t]
  );
  return (
    <div className="bubble_meta_controls">
      {showOption ? (
        <Menu
          open={openMenu}
          onOpen={handleOpen}
          onClose={handleClose}
          target={
            <IconButton
              variant="text gray"
              active={openMenu}
              className={classnames({ open: openMenu })}
              size="small"
            >
              <TreeDotsIcon />
            </IconButton>
          }
          menuClassName="tw-relative tw-overflow-hidden"
        >
          <List className="tw-py-2">
            {canEdit && (
              <ListItem onClick={triggerEditComment}>
                <EditIcon className="tw-fill-theme-900" />
                {t("Edit")}
              </ListItem>
            )}
            {canCreateTask ? (
              <>
                <ListItem onClick={() => setDialogState("create")}>
                  <CheckboxCircleArrowIcon className="tw-fill-theme-900" />
                  {t("Create Task")}
                </ListItem>
                <CreateTaskDialog
                  open={dialogState === "create"}
                  onCancel={closeDialog}
                  onSubmit={onCreateTask}
                  id={id}
                  name=""
                  explainerText={t("A new task containing the comment's text and attachments will be created")}
                  projectId={parent?.project_id}
                />
              </>
            ) : null}
            {canAppend && (
              <>
                <ListItem onClick={() => setDialogState("append")}>
                  <DescriptionIcon className="tw-fill-theme-900" />
                  {t("Append to {{context}}", {
                    context: comment.parent_type === "CalendarEvent" ? "Event" : comment.parent_type,
                  })}
                </ListItem>
                <AppendDialog
                  open={dialogState === "append"}
                  onConfirm={closeDialogAndMenu}
                  onCancel={closeDialog}
                  id={comment.id}
                />
              </>
            )}
            {canDelete && (
              <>
                <ListSeparator />
                <ListItem onClick={() => setDialogState("delete")}>
                  <TrashIcon className="tw-fill-theme-900" />
                  {t("Delete")}
                </ListItem>
                <ConfirmDialog
                  open={dialogState === "delete"}
                  onCancel={closeDialog}
                  onConfirm={triggerDeleteComment}
                  dialogTitle={t("Delete Comment?")}
                  dialogContent={t("The comment will remain in Trash for 30 days.")}
                  cancelBtnText={t("Cancel")}
                  confirmBtnText={t("Delete")}
                  shouldShowCancelButton={true}
                />
              </>
            )}
          </List>
        </Menu>
      ) : null}
    </div>
  );
};

CommentOptions.propTypes = {
  comment: PropTypes.any.isRequired,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
};
